import type { ChartDataset, Point } from "chart.js";
import {
	DUTY_POINT_COLOR,
	DUTY_POINT_LABEL,
	SERIES_COLORS,
	SYSTEM_CURVE_COLOR,
} from "../../data/chart";
import {
	APPLICATION_TYPE_OPTIONS,
} from "../../data/applicationTypes";
import {
	ChartDataRequest,
	ChartPoint,
	ChartSeries,
	PumpSizingFormData,
	SeriesType,
} from "../../types/types";
import { parseNumericValue } from "./helpers";
import { normalizeSeriesLabel } from "./productHelpers";
import { getActiveTdhHelp, getTdhDisplayValue } from "./tdhHelpers";
import { getGpmDisplayValue } from "./gpmHelpers";

type PumpCurveDataset = ChartDataset<"line", Point[]>;

export function buildChartDataRequest(
	formData: PumpSizingFormData,
): ChartDataRequest {
	const help = getActiveTdhHelp(formData);
	const gpmDisplay = getGpmDisplayValue(formData);
	const tdhDisplay = getTdhDisplayValue(formData);

	const pumpType = APPLICATION_TYPE_OPTIONS.find(
		(option) => option.id === formData.applicationType,
	);

	if (!pumpType) {
		throw new Error("Select an application type before loading chart data.");
	}

	const gpmRequirement = parseNumericValue(gpmDisplay, 0);
	const isKnownTdh = formData.tdhData.mode === "known";
	const tdhRequirement = isKnownTdh ? parseNumericValue(tdhDisplay, 0) : null;

	if (gpmRequirement <= 0) {
		throw new Error("GPM value is required for chart data.");
	}

	if (isKnownTdh && (tdhRequirement === null || tdhRequirement <= 0)) {
		throw new Error("TDH value is required for chart data.");
	}

	const pipeMaterial = help?.pipeMaterial || null;
	const pipeDiameter = help ? parseNumericValue(help.pipeDiameter, 0) : 0;
	const totalPipeLength = help
		? parseNumericValue(help.totalLengthOfPipe, 0)
		: 0;
	const staticHead = help ? parseNumericValue(help.staticHead, 0) : 0;

	return {
		PumpType: pumpType.title,
		HazardousLocation: formData.hazardousLocation === true,
		GPMRequirement: gpmRequirement,
		TDHRequirement: tdhRequirement,
		PipeMaterial: pipeMaterial || null,
		PipeDiameterInches: pipeDiameter > 0 ? pipeDiameter : null,
		TotalPipeLengthFeet: totalPipeLength > 0 ? totalPipeLength : null,
		StaticHeadFeet: staticHead > 0 ? staticHead : null,
	};
}

/**
 * Splits sewage from grinder pumps. Matched on a substring so it survives the
 * API using a longer label than the bare word.
 */
export function getSeriesType(series: ChartSeries): SeriesType {
	return (series.type ?? "").toLowerCase().includes("grinder")
		? "grinder"
		: "sewage";
}

export function formatChartTick(value: string | number): string {
	const num = typeof value === "number" ? value : Number.parseFloat(value);
	return Number.isFinite(num) ? num.toFixed(2) : String(value);
}

export function getSeriesColor(index: number): string {
	return SERIES_COLORS[index % SERIES_COLORS.length];
}

/** Lighter fill for Chart.js legend boxes (border stays full-strength). */
export function colorWithAlpha(hex: string, alpha = 0.2): string {
	const normalized = hex.replace("#", "");
	const full =
		normalized.length === 3
			? normalized
					.split("")
					.map((char) => char + char)
					.join("")
			: normalized;

	if (full.length !== 6) {
		return hex;
	}

	const r = Number.parseInt(full.slice(0, 2), 16);
	const g = Number.parseInt(full.slice(2, 4), 16);
	const b = Number.parseInt(full.slice(4, 6), 16);

	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function buildPumpCurveChartData(
	series: ChartSeries[],
	systemCurvePoints: ChartPoint[],
	dutyGpm: number | null,
	dutyTdh: number | null,
	hiddenSeries: ReadonlySet<string> = new Set<string>(),
) {
	let maxGpm = 0;
	let pumpColorIndex = 0;

	const pumpDatasets = series
		// Colours are assigned before hiding so a series keeps its colour
		// regardless of which other curves are toggled off.
		.map((item) => ({ item, color: getSeriesColor(pumpColorIndex++) }))
		.filter(
			({ item }) =>
				!hiddenSeries.has(
					normalizeSeriesLabel(item.display_value),
				),
		)
		.map(({ item, color }) => {
			const points = (item.points ?? []).map((point) => {
				maxGpm = Math.max(maxGpm, point.gpm);
				return { x: point.gpm, y: point.tdh };
			});

			return {
				label: item.display_value || "",
				data: points,
				borderColor: color,
				backgroundColor: colorWithAlpha(color),
				pointRadius: 0,
				// Zero hit radius keeps the curve out of hover detection, so only
				// the duty point can ever open a tooltip.
				pointHitRadius: 0,
				borderWidth: 2,
				tension: 0.2,
			};
		});

	const systemPoints = (systemCurvePoints ?? []).map((point) => {
		maxGpm = Math.max(maxGpm, point.gpm);
		return { x: point.gpm, y: point.tdh };
	});

	if (dutyGpm !== null) {
		maxGpm = Math.max(maxGpm, dutyGpm);
	}

	const datasets: PumpCurveDataset[] = [];

	if (systemPoints.length > 0) {
		datasets.push({
			label: "System Curve",
			data: systemPoints,
			borderColor: SYSTEM_CURVE_COLOR,
			backgroundColor: colorWithAlpha(SYSTEM_CURVE_COLOR),
			pointRadius: 0,
			pointHitRadius: 0,
			borderWidth: 2,
			tension: 0.25,
			order: 1,
		});
	}

	datasets.push(...pumpDatasets);

	if (dutyGpm !== null && dutyTdh !== null) {
		datasets.push({
			label: DUTY_POINT_LABEL,
			data: [{ x: dutyGpm, y: dutyTdh }],
			borderColor: DUTY_POINT_COLOR,
			backgroundColor: colorWithAlpha(DUTY_POINT_COLOR),
			pointRadius: 4,
			pointStyle: "cross",
			pointHoverRadius: 5,
			borderWidth: 1,
			tension: 0,
			order: 0,
			showLine: false,
		});
	}

	return {
		datasets,
		xMax: maxGpm > 0 ? parseNumericValue(String(maxGpm * 1.1)) : undefined,
	};
}
